import { FC, useCallback } from 'react';
import { enqueueSnackbar } from 'notistack';
import { useMutation, useQueryClient } from 'react-query';
import { useTranslation } from 'react-i18next';
import { getErrorMessage } from 'shared/helpers/getErrorMessage';
import { createAssertionsSet } from 'entities/Assertions/api';
import { AssertionsSetEditorType } from 'widgets/Extensions/Details/ui/AssertionSets/ui/AsModals/AssertionsSetEditor/types';
import { AssertionsSetEditor } from './AssertionsSetEditor';

interface Props {
	open: boolean;
	handleClose: () => void;
}

const CreateAssertionsSetModal: FC<Props> = ({ open, handleClose }) => {
	const { t } = useTranslation();
	const queryClient = useQueryClient();

	const { mutate } = useMutation({
		mutationKey: ['create/assertion-sets'],
		mutationFn: createAssertionsSet,
	});

	const handleSubmitCreation = useCallback(
		(values: AssertionsSetEditorType) => {
			mutate(values, {
				onSuccess: async () => {
					await queryClient.invalidateQueries({ queryKey: ['get/assertion-sets'] });
					enqueueSnackbar({
						message: t('assertions.snackbar.success_assertions_set_creation'),
						variant: 'success',
					});
					handleClose();
				},
				onError: (err) => {
					const errorMessage = getErrorMessage(err);
					enqueueSnackbar({
						message: errorMessage,
						variant: 'error',
					});
				},
			});
		},
		[mutate, t],
	);

	return (
		<AssertionsSetEditor
			title={t('assertions.modal.add_as_set_title')}
			open={open}
			handleSubmit={handleSubmitCreation}
			handleClose={handleClose}
		/>
	);
};

export default CreateAssertionsSetModal;
